import { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { Box } from '@alf/uikit';
import { useGeneralRoutes } from '@namespace/cms';
import {
  formatAsUriParameters,
  preserveSearchParams
} from '@namespace/helpers';
import { useI18n } from '@namespace/i18n';
import { useHistory } from '@namespace/router';
import {
  CustomFilterLink,
  FavoritesLink,
  FilterMenuList
} from '../../../common';
import { MODE } from '../../../constants';
import { useGetFilterParams } from '../../../hooks/useGetFilterParams';
import { useGetPathParams } from '../../../hooks/useGetPathParams';
import { useSportsBookDataState } from '../../../store';
import { useAdvFilterStore } from '../../../storeAdvFilter';
import { AdvFilter } from '../../AdvancedFilter';
import { MenuHeader } from './components/MenuHeader';

import styles from './index.module.css';

export const PreMatchFilterList = () => {
  const { t } = useI18n();
  const { push } = useHistory();
  const {
    preMatchFilter,
    preMatchSport,
    preMatchFavorites,
    preMatchCategory,
    preMatchTournament
  } = useGeneralRoutes();

  const {
    sportSlug: pathSportSlug,
    activeSport: pathActiveSport,
    categoryId: pathCategoryId,
    tournamentId
  } = useGetPathParams(MODE.PRE_MATCH, false);
  const {
    sportSlug: filterSportSlug,
    activeSport: filterActiveSport,
    tournamentIds
  } = useGetFilterParams(preMatchFilter, MODE.PRE_MATCH);
  const { isSportsBookDataLoaded } = useSportsBookDataState();

  const activeSport = pathActiveSport ?? filterActiveSport;
  const { sportId: activeSportId } = activeSport ?? {};
  const sportSlug = pathSportSlug ?? filterSportSlug;

  const activeTournamentIds = useMemo(
    () => (tournamentId ? [tournamentId] : tournamentIds),
    [tournamentId, tournamentIds]
  );

  const isAdvFilterOpened = useAdvFilterStore('isAdvFilterOpened');
  const advFilter = useAdvFilterStore('advFilter');

  const baseCategoryPath = `${preMatchCategory}/${sportSlug}`;
  const baseTournamentPath = `${preMatchTournament}/${sportSlug}`;

  const onFilterChange = useCallback(
    ({ categoryId, tournaments = [] }) => {
      let newPath;

      if (categoryId != null) {
        // go to category
        newPath = `${preMatchCategory}/${sportSlug}/${categoryId}`;
      } else if (tournaments.length === 1) {
        // go to tournament
        newPath = `${preMatchTournament}/${sportSlug}/${tournaments[0]}`;
      } else {
        // go to generic filter (multiple tournaments only)
        const searchParams = preserveSearchParams(['timeFilter']);
        const filter = formatAsUriParameters({ tournaments }, true);
        const search = searchParams ? `${filter}&${searchParams}` : filter;
        newPath = `${preMatchFilter}/${sportSlug}/?${search}`;
      }

      return push(newPath);
    },
    [preMatchCategory, preMatchFilter, preMatchTournament, push, sportSlug]
  );

  // prevent initializing FilterMenuList with undefined data
  if (!isSportsBookDataLoaded) {
    return null;
  }

  return (
    <Box
      data-role="preMatchFilterList preMathList"
      direction="column"
      className={clsx(
        styles.preMatchList,
        isAdvFilterOpened && styles.advFilterOpened
      )}
    >
      <MenuHeader
        title="title"
        classNames={{ headerClassName: styles.menuHeaderPreMatch }}
        dataRole="prematch-sport"
      />
      {isAdvFilterOpened && advFilter != null ? (
        <AdvFilter />
      ) : (
        <>
          <FavoritesLink
            to={preMatchFavorites}
            title={t('sportsbook.favorites')}
            className={styles.favoriteItem}
          />
          {advFilter?.length > 0 && (
            <CustomFilterLink className={styles.favoriteItem} />
          )}
          <FilterMenuList
            serviceId={MODE.PRE_MATCH}
            activeSportId={activeSportId}
            activeCategoryId={pathCategoryId}
            tournamentIds={activeTournamentIds}
            baseSportPath={preMatchSport}
            baseCategoryPath={baseCategoryPath}
            baseTournamentPath={baseTournamentPath}
            onChange={onFilterChange}
          />
        </>
      )}
    </Box>
  );
};
